import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { Tooltip } from 'primereact/tooltip';
import { ProgressSpinner } from 'primereact/progressspinner';
import * as XLSX from 'xlsx';
import ApiService, { handleApiError } from '../../services/api';
import {
  FeeSchedule,
  FeeScheduleBulkEntry,
  FeeScheduleStagedFamily,
  FeeScheduleStageResponse,
  FeeScheduleStageRow,
} from '../../types';
import { usePracticeData } from '../../hooks/usePracticeData';

type UploadMode = 'master_group' | 'payer_primary';

const MODE_OPTIONS: { label: string; value: UploadMode }[] = [
  { label: 'Master Payer Groups', value: 'master_group' },
  { label: 'Insurance Payer Primaries', value: 'payer_primary' },
];

interface FeeScheduleUploadProps {
  accountNumber: string;
  /** Optional pre-fetched master payer group names (used only as a fallback). */
  masterPayorGroups?: string[];
  /** Existing saved fee schedules — used to seed the rate editor for no_data rows. */
  existingFeeSchedules?: FeeSchedule[];
  onCommitted?: () => void;
  /** Fired whenever the component gains or loses uncommitted staged changes. */
  onHasStagedChanges?: (dirty: boolean) => void;
}

interface ParsedGrid {
  headers: string[];
  rows: FeeScheduleStageRow[];
  /** Header names that appeared in more than one column (their values were averaged). */
  duplicateHeaders: string[];
  /** Normalized J-Codes that appeared in more than one row (may surface as conflicts). */
  duplicateJcodes: string[];
  /** Count of cells whose negative value was clamped up to 0. */
  negativeClamped: number;
  /** Count of cells whose value exceeded the max and was clamped down. */
  maxClamped: number;
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_RATE = 999999.9999;

// Mirrors the backend's `_norm_jcode` (strip → upper → remove spaces) so the
// client-side relevant-J-code filter matches exactly what staging would keep.
const normJcode = (s: unknown): string => String(s ?? '').trim().toUpperCase().replace(/ /g, '');

// Result of parsing a single rate cell. We return the clamp flags (rather than clamping
// silently) so the caller can warn the user when their data had to be rewritten — e.g. a
// negative typo becoming $0, which then silently falls back to the Medicare rate.
interface ParsedRate {
  value: number | null;
  clampedNegative: boolean;
  clampedMax: boolean;
}

const parseRateCell = (cell: unknown): ParsedRate => {
  if (cell === '' || cell === null || cell === undefined) {
    return { value: null, clampedNegative: false, clampedMax: false };
  }
  let n: number;
  if (typeof cell === 'number') {
    if (!Number.isFinite(cell)) return { value: null, clampedNegative: false, clampedMax: false };
    n = cell;
  } else {
    n = parseFloat(String(cell).replace(/[$,\s]/g, ''));
    if (Number.isNaN(n)) return { value: null, clampedNegative: false, clampedMax: false };
  }
  let clampedNegative = false;
  let clampedMax = false;
  if (n < 0) {
    n = 0;
    clampedNegative = true;
  }
  if (n > MAX_RATE) {
    n = MAX_RATE;
    clampedMax = true;
  }
  return { value: n, clampedNegative, clampedMax };
};

const parseGrid = (wb: XLSX.WorkBook): ParsedGrid => {
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const raw: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  if (raw.length === 0) {
    throw new Error('File contains no data.');
  }

  const headerRow = (raw[0] as unknown[]).map((h) => String(h ?? '').trim());
  // First column = J-Code; remaining columns = payer-group headers.
  const colHeaders = headerRow.slice(1);
  if (colHeaders.every((h) => h === '')) {
    throw new Error('Could not detect any payer-group columns. The first column should be J-Code, followed by one column per payer group.');
  }

  // Detect duplicate column headers up front. Two columns with the SAME header name are
  // averaged per row below; that can silently invent a value (e.g. two "Aetna" columns of
  // 4.3 and 20.3 -> 12.3), so we surface the affected names to the user rather than hide it.
  const headerCounts: Record<string, number> = {};
  for (const h of colHeaders) {
    if (h) headerCounts[h] = (headerCounts[h] ?? 0) + 1;
  }
  const duplicateHeaders = Object.keys(headerCounts).filter((h) => headerCounts[h] > 1);

  let negativeClamped = 0;
  let maxClamped = 0;
  // How many rows each normalized J-Code appears in, to warn about duplicate J-Code rows.
  const jcodeRowCounts: Record<string, number> = {};

  const rows: FeeScheduleStageRow[] = [];
  for (let i = 1; i < raw.length; i++) {
    const line = raw[i] as unknown[];
    const jcode = String(line[0] ?? '').trim();
    if (!jcode) continue;
    // Skip rows where the first cell is a header-like label accidentally included
    if (jcode.toLowerCase() === 'j-code' || jcode.toLowerCase() === 'j-code (hcpcs)' || jcode.toLowerCase() === 'hcpcs') continue;
    jcodeRowCounts[normJcode(jcode)] = (jcodeRowCounts[normJcode(jcode)] ?? 0) + 1;
    // Accumulate all numeric values per header name — duplicate columns (same name)
    // are averaged so that e.g. two "Aetna" columns produce one averaged rate.
    const accum: Record<string, number[]> = {};
    for (let c = 0; c < colHeaders.length; c++) {
      const h = colHeaders[c];
      if (!h) continue;
      if (!(h in accum)) accum[h] = [];
      const parsed = parseRateCell(line[c + 1]);
      if (parsed.clampedNegative) negativeClamped += 1;
      if (parsed.clampedMax) maxClamped += 1;
      if (parsed.value !== null) accum[h].push(parsed.value);
    }
    const rates: Record<string, number | null> = {};
    for (const [h, vals] of Object.entries(accum)) {
      rates[h] = vals.length > 0
        ? Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10000) / 10000
        : null;
    }
    rows.push({ jcode, rates });
  }

  // A file whose first row is a real header but whose only "data" rows were all skipped
  // (blank/header-like first cells) yields nothing usable — tell the user rather than
  // silently sending an empty payload.
  if (rows.length === 0) {
    throw new Error(
      'No J-Code rows were found. Make sure the first column contains J-Codes (HCPCS) and that the first row is the column header.',
    );
  }

  const duplicateJcodes = Object.keys(jcodeRowCounts).filter((jc) => jcodeRowCounts[jc] > 1);

  // Deduplicate headers — duplicate column names are averaged above, so only one
  // entry should be sent to the backend per unique header name.
  return {
    headers: Array.from(new Set(colHeaders.filter((h) => h !== ''))),
    rows,
    duplicateHeaders,
    duplicateJcodes,
    negativeClamped,
    maxClamped,
  };
};

// Shared, low-key status pill used both on the staged-family list rows and inside the
// rate editor. One palette keeps the whole feature visually consistent: soft tinted
// background + matching text + a small status dot (or icon), never a loud solid fill.
// Each tone carries meaning: new = brand-new data, same = matches what's saved,
// changed = differs from saved, conflict = must be resolved, warning = nothing found,
// edited = user-entered.
type PillTone = 'new' | 'same' | 'changed' | 'conflict' | 'warning' | 'edited';

const PILL_PALETTE: Record<PillTone, { color: string; bg: string; border: string; dot?: string }> = {
  new: { color: '#0f766e', bg: '#f0fdfa', border: '#99f6e4', dot: '#14b8a6' },
  same: { color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', dot: '#3b82f6' },
  changed: { color: '#b45309', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b' },
  conflict: { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca', dot: '#ef4444' },
  warning: { color: '#92400e', bg: '#fffbeb', border: '#fde68a' },
  edited: { color: '#6d28d9', bg: '#f5f3ff', border: '#ddd6fe', dot: '#8b5cf6' },
};

const StatusPill = ({
  tone,
  label,
  title,
  icon,
  style,
}: {
  tone: PillTone;
  label: string;
  title?: string;
  /** Optional PrimeIcons class rendered instead of the status dot. */
  icon?: string;
  style?: CSSProperties;
}) => {
  const p = PILL_PALETTE[tone];
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        padding: '0.1rem 0.5rem',
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        lineHeight: 1.6,
        color: p.color,
        background: p.bg,
        border: `1px solid ${p.border}`,
        borderRadius: 999,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon ? (
        <i className={icon} style={{ fontSize: '0.7rem' }} />
      ) : p.dot ? (
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: p.dot, flexShrink: 0 }} />
      ) : null}
      {label}
    </span>
  );
};

const statusTag = (
  status: string,
  edited?: boolean,
  hasExisting?: boolean,
  allZeroRates?: boolean,
  matchedUnchanged?: boolean,
) => {
  if (edited && status === 'no_data') {
    return <StatusPill tone="edited" label="Edited" title="You entered these rates" />;
  }
  switch (status) {
    case 'matched':
      if (matchedUnchanged) {
        // The file carried rates for this product family, but every one already equals
        // what's saved — applying would change nothing. Say so instead of implying new data.
        return (
          <StatusPill
            tone="same"
            label="Matches saved rates"
            title="Every rate in the file already equals what's saved"
          />
        );
      }
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
          <StatusPill
            tone="new"
            label="New rates from file"
            title="The file brings new or updated rates for this product family"
          />
          {allZeroRates && (
            <i
              className="pi pi-exclamation-circle text-orange-400 fs-upload-zero-rate"
              style={{ fontSize: '0.85rem', verticalAlign: 'middle', cursor: 'default' }}
              data-pr-tooltip="New rates found, but all are $0.00 — Medicare fallback will be used in calculations"
              data-pr-position="top"
            />
          )}
        </span>
      );
    case 'conflict':
      return (
        <StatusPill
          tone="conflict"
          label="Conflict"
          icon="pi pi-exclamation-triangle"
          title="Conflicting J-Code rates in the file — resolve before saving"
        />
      );
    case 'no_data':
    default:
      return hasExisting ? (
        <StatusPill
          tone="same"
          label="Existing rates kept"
          title="No new rates in the file; your saved rates are kept"
        />
      ) : (
        <StatusPill
          tone="warning"
          label="No rates found"
          icon="pi pi-exclamation-triangle"
          title="No rates in the file for this product family"
        />
      );
  }
};

// ── Rate editor dialog ────────────────────────────────────────────────────────

interface RateEditorProps {
  row: FeeScheduleStagedFamily;
  allGroups: string[];
  initialRates: Record<string, number>;
  missingGroups: string[];
  /** True when this row has a payer conflict — shows an informational note. */
  isConflict?: boolean;
  /** True when this no_data row has saved rates in the DB (seeded into editor). */
  hasExistingRates?: boolean;
  /** Currently saved rates for this product family (master group -> rate). Drives the
   *  per-payer "New / Same / Changed" badges so the user can see, inside the editor,
   *  which incoming rates differ from what's already saved. */
  existingRates?: Record<string, number>;
  /** Upload mode this staging came from — drives plan-level vs group-level editing. */
  mode?: UploadMode;
  /** payer_primary only: this row's rates keyed by insurance plan (primary). */
  planRates?: Record<string, number>;
  /** payer_primary only: canonical insurance plan -> master payer group. */
  planToGroup?: Record<string, string>;
  onSave: (pid: number, rates: Record<string, number>) => void;
  onClose: () => void;
}

const round4 = (n: number) => Math.round(n * 10000) / 10000;

const formatUsd = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

const RateEditorDialog: React.FC<RateEditorProps> = ({
  row,
  allGroups,
  initialRates,
  missingGroups,
  isConflict,
  hasExistingRates,
  existingRates,
  mode,
  planRates,
  planToGroup,
  onSave,
  onClose,
}) => {
  // Plan-level editing applies only when we uploaded by insurance plan AND this row
  // actually has plan rates from the file. no_data rows (no plan rates) fall back to
  // group-level editing so users can still seed rates against the master group.
  const usePlanLevel =
    mode === 'payer_primary' &&
    !!planToGroup &&
    !!planRates &&
    Object.keys(planRates).length > 0;

  // The insurance plans belonging to this product family, grouped by master payer group
  // for display. Storage still collapses to the master group (averaged) on save.
  const plansByGroup = useMemo<Record<string, string[]>>(() => {
    if (!usePlanLevel) return {};
    const map: Record<string, string[]> = {};
    for (const plan of Object.keys(planRates ?? {})) {
      const g = planToGroup?.[plan] ?? '(unmapped)';
      (map[g] ??= []).push(plan);
    }
    for (const g of Object.keys(map)) {
      map[g].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    return map;
  }, [usePlanLevel, planRates, planToGroup]);

  const [localPlanRates, setLocalPlanRates] = useState<Record<string, number>>(() => {
    const seed: Record<string, number> = {};
    for (const plan of Object.keys(planRates ?? {})) {
      seed[plan] = planRates?.[plan] ?? 0;
    }
    return seed;
  });

  const [localRates, setLocalRates] = useState<Record<string, number>>(() => {
    // Seed: start from ingested rates, default missing groups to 0.
    const seed: Record<string, number> = {};
    for (const g of allGroups) {
      seed[g] = initialRates[g] ?? 0;
    }
    return seed;
  });

  const missingSet = useMemo(() => new Set(missingGroups), [missingGroups]);
  const fromFile = useMemo(
    () => allGroups.filter((g) => !missingSet.has(g)),
    [allGroups, missingSet],
  );
  const missingArr = useMemo(
    () => allGroups.filter((g) => missingSet.has(g)),
    [allGroups, missingSet],
  );

  // Per-payer comparison against the account's already-saved rate for this product
  // family, so the editor can flag each rate as brand New, unchanged (Same), or
  // Changed from what's stored. Compares the live editor value (rounded to 4 dp to
  // avoid float noise) against existingRates[group].
  const renderRateStatus = useCallback(
    (group: string) => {
      const savedRaw = existingRates?.[group];
      const hasSaved = savedRaw !== undefined && savedRaw !== null;
      const current = round4(localRates[group] ?? 0);

      // No rate entered ($0 / empty) → this payer's volume falls back to the Medicare rate
      // in weighted NCR. Highlight it so gaps are obvious right where they're fixed. This is
      // the same signal surfaced on the scenario results (the amber fee-schedule-gap marker).
      if (current === 0) {
        return (
          <span
            className="fee-fallback-pill"
            data-pr-tooltip="No rate — this payer falls back to the Medicare rate in weighted NCR"
            data-pr-position="top"
            style={{
              marginLeft: '0.4rem',
              fontSize: '0.65rem',
              padding: '0.05rem 0.45rem',
              borderRadius: '999px',
              background: '#fef3c7',
              color: '#92400e',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            ⚠ Medicare fallback
          </span>
        );
      }

      let tone: PillTone;
      let label: string;
      let title: string;
      if (!hasSaved) {
        tone = 'new';
        label = 'New';
        title = 'No saved rate for this payer yet';
      } else if (current === round4(savedRaw as number)) {
        tone = 'same';
        label = 'Same';
        title = 'Matches your saved rate';
      } else {
        tone = 'changed';
        label = 'Changed';
        title = `Was ${formatUsd(round4(savedRaw as number))}`;
      }

      return (
        <StatusPill
          tone={tone}
          label={label}
          title={title}
          style={{ marginLeft: '0.4rem', fontSize: '0.65rem', padding: '0.05rem 0.45rem' }}
        />
      );
    },
    [existingRates, localRates],
  );

  const handleChange = (group: string, value: number | null) => {
    setLocalRates((prev) => ({ ...prev, [group]: value ?? 0 }));
  };

  const handlePlanChange = (plan: string, value: number | null) => {
    setLocalPlanRates((prev) => ({ ...prev, [plan]: value ?? 0 }));
  };

  const isDirty = useMemo(() => {
    if (usePlanLevel) {
      return Object.keys(planRates ?? {}).some(
        (plan) => (localPlanRates[plan] ?? 0) !== (planRates?.[plan] ?? 0),
      );
    }
    return allGroups.some((g) => (localRates[g] ?? 0) !== (initialRates[g] ?? 0));
  }, [usePlanLevel, planRates, localPlanRates, allGroups, localRates, initialRates]);

  // Collapse edited plan rates to a single value per master group (average across the
  // plans in that group), matching how the backend stores payer_primary uploads.
  const handleSave = () => {
    if (usePlanLevel) {
      const sums: Record<string, { sum: number; n: number }> = {};
      for (const plan of Object.keys(planRates ?? {})) {
        const g = planToGroup?.[plan];
        if (!g) continue;
        const v = localPlanRates[plan] ?? 0;
        const cur = sums[g] ?? { sum: 0, n: 0 };
        sums[g] = { sum: cur.sum + v, n: cur.n + 1 };
      }
      const groupRates: Record<string, number> = {};
      for (const [g, { sum, n }] of Object.entries(sums)) {
        groupRates[g] = n > 0 ? round4(sum / n) : 0;
      }
      onSave(row.product_family_id, groupRates);
      return;
    }
    onSave(row.product_family_id, localRates);
  };

  const header = (
    <div>
      <div style={{ fontWeight: 600, fontSize: '1rem' }}>{row.product_family_name}</div>
      <div style={{ fontWeight: 400, fontSize: '0.8125rem', color: 'var(--text-color-secondary)', marginTop: '0.15rem' }}>
        {[row.jcodes.join(', '), row.category].filter(Boolean).join(' · ')}
      </div>
    </div>
  );

  return (
    <Dialog
      header={header}
      visible
      style={{ width: '480px' }}
      modal
      onHide={onClose}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button label="Cancel" severity="secondary" text onClick={onClose} />
          <Button
            label="Save to staging"
            icon="pi pi-check"
            onClick={handleSave}
            disabled={!isDirty}
            title={!isDirty ? 'No changes have been made' : undefined}
          />
        </div>
      }
    >
      <Tooltip target=".fee-fallback-pill" />
      {isConflict && (
        <Message
          severity="warn"
          text="This product family has conflicting J-Code rates in the file, so it can't be saved. Fix the conflicting rows in your file and re-upload."
          className="w-full mb-3"
        />
      )}
      {row.status === 'no_data' && (
        <Message
          severity={hasExistingRates ? 'info' : 'warn'}
          text={
            hasExistingRates
              ? 'No new rates were found in the file for this product family. Your currently saved rates are shown below — edit to override, or close to keep them unchanged.'
              : 'No rates were found in the file for this product family. Enter rates below, or leave at $0.00 to use the Medicare fallback rate.'
          }
          className="w-full mb-3"
        />
      )}

      {usePlanLevel ? (
        <>
          <p className="m-0 mb-3" style={{ fontSize: '0.8125rem', color: 'var(--text-color-secondary)' }}>
            Rates are entered per insurance plan but saved at the master payer group level.
          </p>
          <div className="fee-schedule-rates">
            {Object.keys(plansByGroup)
              .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
              .map((g) => (
                <div key={g} className="fee-schedule-plan-group">
                  <div className="fee-schedule-plan-group-header">{g}</div>
                  {plansByGroup[g].map((plan) => (
                    <div className="fee-schedule-rate-row fee-schedule-plan-row" key={plan}>
                      <span className="fee-schedule-payer-name">{plan}</span>
                      <InputNumber
                        value={localPlanRates[plan] ?? 0}
                        onValueChange={(e) => handlePlanChange(plan, e.value ?? null)}
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        minFractionDigits={2}
                        maxFractionDigits={4}
                        min={0}
                        max={999999.9999}
                        className="fee-schedule-rate-input"
                      />
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </>
      ) : (
        <div className="fee-schedule-rates">
          {fromFile.length > 0 && (
            <p className="m-0 mb-2" style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--text-color-secondary)' }}>
              NEW RATES FROM FILE
            </p>
          )}
          {fromFile.map((g) => (
            <div className="fee-schedule-rate-row" key={g}>
              <span className="fee-schedule-payer-name">
                {g}
                {renderRateStatus(g)}
              </span>
              <InputNumber
                value={localRates[g] ?? 0}
                onValueChange={(e) => handleChange(g, e.value ?? null)}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2}
                maxFractionDigits={4}
                min={0}
                max={999999.9999}
                className="fee-schedule-rate-input"
              />
            </div>
          ))}

          {missingArr.length > 0 && (
            <>
              <p className="m-0 mt-3 mb-2" style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--text-color-secondary)' }}>
                NOT IN FILE — ENTER A RATE OR LEAVE AT $0.00 FOR MEDICARE FALLBACK
              </p>
              {missingArr.map((g) => (
                <div className="fee-schedule-rate-row" key={g}>
                  <span className="fee-schedule-payer-name">
                    {g}
                    {renderRateStatus(g)}
                  </span>
                  <InputNumber
                    value={localRates[g] ?? 0}
                    onValueChange={(e) => handleChange(g, e.value ?? null)}
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    minFractionDigits={2}
                    maxFractionDigits={4}
                    min={0}
                    max={999999.9999}
                    className="fee-schedule-rate-input"
                  />
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </Dialog>
  );
};

const FeeScheduleUpload: React.FC<FeeScheduleUploadProps> = ({
  accountNumber,
  masterPayorGroups: masterPayorGroupsProp = [],
  existingFeeSchedules,
  onCommitted,
  onHasStagedChanges,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Full groupings map: { masterGroup → [primaryName, ...] }
  const [payerGroupingsMap, setPayerGroupingsMap] = useState<Record<string, string[]>>({});
  const [uploadMode, setUploadMode] = useState<UploadMode>('master_group');

  const [fileName, setFileName] = useState('');
  const [stageResult, setStageResult] = useState<FeeScheduleStageResponse | null>(null);
  const [isStaging, setIsStaging] = useState(false);
  const { isDemoMode } = usePracticeData();
  const [isCommitting, setIsCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // pid -> rate overrides entered via the rate editor dialog
  const [editOverrides, setEditOverrides] = useState<Record<number, Record<string, number>>>({});
  const [editorRow, setEditorRow] = useState<FeeScheduleStagedFamily | null>(null);
  // Tracks which upload mode was active when staging succeeded (for the results header).
  const [stagedMode, setStagedMode] = useState<UploadMode | null>(null);

  // Non-fatal warnings from parsing/matching the uploaded sheet. Surfaced to the user so
  // no silent data rewrite (duplicate columns averaged, negatives zeroed, J-Codes ignored)
  // ever goes unnoticed. Recomputed on every upload; null when there's nothing to warn about.
  const [parseWarnings, setParseWarnings] = useState<{
    duplicateHeaders: string[];
    duplicateJcodes: string[];
    negativeClamped: number;
    maxClamped: number;
    unmatchedJcodes: string[];
    allUnmatched: boolean;
  } | null>(null);

  const uploadCounterRef = useRef(0);

  // Cache of the account's relevant (carried-PF) J-codes, fetched once per account.
  // Used to pre-filter an uploaded sheet so only rows that matter are staged.
  const relevantJcodesRef = useRef<{ account: string; set: Set<string> } | null>(null);

  // Build a pid → reimbursements map from the currently saved fee schedules so the
  // rate editor for no_data rows can seed from real values instead of all-$0.
  const existingRatesById = useMemo<Record<number, Record<string, number>>>(() => {
    const map: Record<number, Record<string, number>> = {};
    for (const fs of (existingFeeSchedules ?? [])) {
      if (Object.keys(fs.reimbursements).length > 0) {
        map[fs.product_family_id] = fs.reimbursements;
      }
    }
    return map;
  }, [existingFeeSchedules]);

  // Master payer group names (sorted).
  const resolvedGroups = useMemo(
    () =>
      Object.keys(payerGroupingsMap).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      ),
    [payerGroupingsMap],
  );

  // Flat list of all payer primaries across all groups (sorted, deduped).
  const resolvedPrimaries = useMemo(() => {
    const seen = new Set<string>();
    const all: string[] = [];
    for (const primaries of Object.values(payerGroupingsMap)) {
      for (const p of primaries) {
        if (!seen.has(p)) {
          seen.add(p);
          all.push(p);
        }
      }
    }
    return all.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [payerGroupingsMap]);

  // Columns used for the template download and stage matching.
  const templateColumns = uploadMode === 'payer_primary' ? resolvedPrimaries : resolvedGroups;

  // Notify parent whenever staged state appears or disappears.
  useEffect(() => {
    onHasStagedChanges?.(stageResult !== null);
  }, [stageResult, onHasStagedChanges]);

  // Always fetch the full payer groupings map so we have primaries available
  // for the payer_primary template/mode, regardless of the prop.
  useEffect(() => {
    if (!accountNumber) return;
    ApiService.getPayerGroupings(accountNumber)
      .then((response) => {
        // Response shape: { [masterPayorGroup]: string[] } (possibly nested under .data)
        const source: unknown =
          response && typeof response === 'object' && 'data' in response
            ? (response as { data: unknown }).data
            : response;
        if (source && typeof source === 'object' && !Array.isArray(source)) {
          const raw = source as Record<string, unknown>;
          const cleaned: Record<string, string[]> = {};
          for (const [k, v] of Object.entries(raw)) {
            if (k === 'MasterPayors') continue;
            cleaned[k] = Array.isArray(v) ? (v as string[]) : [];
          }
          setPayerGroupingsMap(cleaned);
        }
      })
      .catch(() => {
        // Non-fatal — fall back to prop if available.
        if (masterPayorGroupsProp.length > 0) {
          const fallback: Record<string, string[]> = {};
          for (const g of masterPayorGroupsProp) fallback[g] = [];
          setPayerGroupingsMap(fallback);
        }
      });
  }, [accountNumber, masterPayorGroupsProp]);

  const resetState = useCallback(() => {
    setFileName('');
    setStageResult(null);
    setStagedMode(null);
    setError(null);
    setSuccess(null);
    setEditOverrides({});
    setEditorRow(null);
    setParseWarnings(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleDiscard = useCallback(() => {
    if (!stageResult) {
      resetState();
      return;
    }
    confirmDialog({
      message: 'Discard the staged fee schedule and all unsaved edits?',
      header: 'Discard Upload',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectLabel: 'Stay',
      acceptClassName: 'p-button-danger',
      accept: resetState,
    });
  }, [stageResult, resetState]);

  const handleDownloadTemplate = useCallback(() => {
    // Seed a handful of clearly-fake example rows so users can see what a valid
    // file looks like (column-1 = J-Code, matching payer headers, numeric rates)
    // without us shipping their real data. The 'J...-SAMPLE' codes can't collide
    // with a carried J-code, so they're safely dropped at staging if left in.
    // Every row has a J-Code and mostly-filled rates; a few individual cells are
    // left blank to show that rates are optional per cell.
    const makeRow = (jcode: string, baseRate: number, rowIndex: number): Record<string, string> => {
      const row: Record<string, string> = { 'J-Code (HCPCS)': jcode };
      templateColumns.forEach((col, i) => {
        // Leave a scattered cell blank per row (deterministic) so the example
        // shows that individual cells can be empty; fill the rest with a value
        // that varies slightly per column to look realistic. The very first data
        // cell (first row, first column) is always filled as a complete anchor.
        const leaveBlank =
          templateColumns.length > 1 &&
          i === (rowIndex % templateColumns.length) &&
          !(rowIndex === 0 && i === 0);
        row[col] = leaveBlank ? '' : (baseRate + i * 12.5).toFixed(2);
      });
      return row;
    };
    const rows = [
      makeRow('J0001-SAMPLE', 1234.56, 0),
      makeRow('J0002-SAMPLE', 845.1, 1),
      makeRow('J0003-SAMPLE', 2310, 2),
      makeRow('J0004-SAMPLE', 560.25, 3),
      makeRow('J0005-SAMPLE', 1780.4, 4),
      makeRow('J0006-SAMPLE', 999.99, 5),
      makeRow('J0007-SAMPLE', 430.75, 6),
      makeRow('J0008-SAMPLE', 1520, 7),
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 18 }, ...templateColumns.map(() => ({ wch: 24 }))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fee Schedule');
    XLSX.writeFile(wb, 'fee-schedule-template.xlsx');
  }, [templateColumns]);

  // Fetch (and cache per account) the set of J-codes tied to carried product
  // families. Returns null if the set can't be determined, so callers can fall
  // back to sending the full sheet rather than dropping every row.
  const getRelevantJcodeSet = useCallback(async (): Promise<Set<string> | null> => {
    const cached = relevantJcodesRef.current;
    if (cached && cached.account === accountNumber) return cached.set;
    try {
      const res = await ApiService.getRelevantJCodes(accountNumber);
      const set = new Set((res.jcodes || []).map(normJcode));
      relevantJcodesRef.current = { account: accountNumber, set };
      return set;
    } catch {
      return null;
    }
  }, [accountNumber]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const allowedExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExt = file.name.includes('.') ? '.' + file.name.split('.').pop()!.toLowerCase() : '';
      if (!allowedExtensions.includes(fileExt)) {
        setError(
          `"${file.name}" is not a supported file type. Please upload an Excel file (.xlsx or .xls) or a CSV (.csv).`
        );
        setStageResult(null);
        e.target.value = '';
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 10 MB.`);
        setStageResult(null);
        e.target.value = '';
        return;
      }

      uploadCounterRef.current += 1;
      const thisUpload = uploadCounterRef.current;

      setFileName(file.name);
      setError(null);
      setSuccess(null);
      setStageResult(null);
      setStagedMode(null);
      setEditOverrides({});
      setParseWarnings(null);
      setIsStaging(true);

      const reader = new FileReader();
      reader.onload = async (evt) => {
        if (uploadCounterRef.current !== thisUpload) return;
        // Yield to let React/browser paint the spinner before XLSX.read() blocks the thread.
        await new Promise<void>((resolve) => { setTimeout(resolve, 0); });
        if (uploadCounterRef.current !== thisUpload) return;
        try {
          const data = new Uint8Array(evt.target?.result as ArrayBuffer);
          let wb: XLSX.WorkBook;
          try {
            wb = XLSX.read(data, { type: 'array' });
          } catch {
            setError(
              'This file could not be read as a spreadsheet. Please upload a valid Excel file (.xlsx or .xls) or a CSV — not a Numbers, ODS, or other format.'
            );
            setIsStaging(false);
            return;
          }
          const grid = parseGrid(wb);
          // Pre-filter to only J-codes tied to carried product families. This is the
          // one extra round-trip that keeps the staging payload bounded — rows whose
          // J-code wouldn't match anything are dropped client-side. On fetch failure
          // we fall back to sending the full sheet (backend still ignores extras).
          const relevant = await getRelevantJcodeSet();
          if (uploadCounterRef.current !== thisUpload) return;
          const rowsToStage = relevant
            ? grid.rows.filter((r) => relevant.has(normJcode(r.jcode)))
            : grid.rows;

          // Surface non-fatal data issues so the user is never silently given rewritten or
          // dropped data. Unmatched J-Codes (present in the file but tied to none of the
          // account's product families) can only be known when we have the relevant set.
          const unmatchedJcodes = relevant
            ? Array.from(
                new Set(
                  grid.rows
                    .map((r) => normJcode(r.jcode))
                    .filter((jc) => jc && !relevant.has(jc)),
                ),
              ).sort()
            : [];
          // If we have the relevant set and NOTHING matched, the file is almost certainly
          // malformed (wrong column order/header row) or for the wrong account.
          const allUnmatched = !!relevant && grid.rows.length > 0 && rowsToStage.length === 0;
          setParseWarnings({
            duplicateHeaders: grid.duplicateHeaders,
            duplicateJcodes: grid.duplicateJcodes,
            negativeClamped: grid.negativeClamped,
            maxClamped: grid.maxClamped,
            unmatchedJcodes,
            allUnmatched,
          });

          const response = await ApiService.stageFeeScheduleUpload(accountNumber, {
            headers: grid.headers,
            rows: rowsToStage,
            mode: uploadMode,
          });
          if (uploadCounterRef.current !== thisUpload) return;
          setStageResult(response);
          setStagedMode(uploadMode);
        } catch (err) {
          if (uploadCounterRef.current !== thisUpload) return;
          setError(err instanceof Error ? err.message : handleApiError(err));
        } finally {
          if (uploadCounterRef.current === thisUpload) setIsStaging(false);
        }
      };
      reader.onerror = () => {
        if (uploadCounterRef.current !== thisUpload) return;
        setError('Could not read this file. Make sure it is a valid Excel (.xlsx/.xls) or CSV file.');
        setIsStaging(false);
      };
      reader.readAsArrayBuffer(file);
      e.target.value = '';
    },
    [accountNumber, uploadMode, getRelevantJcodeSet],
  );

  const hasConflicts = !!stageResult && stageResult.conflicts.length > 0;

  const committableCount = useMemo(() => {
    if (!stageResult) return 0;
    // Conflict rows are never committable — the user must fix the file and re-upload.
    return stageResult.rows.filter((r) => {
      if (r.status === 'no_data') return editOverrides[r.product_family_id] !== undefined;
      if (r.status === 'conflict') return false;
      return true; // 'matched'
    }).length;
  }, [stageResult, editOverrides]);

  // Any conflict blocks the whole upload: the user resolves it in their file and re-uploads.
  // Demo accounts can stage an upload (a read-only dry run) but never commit it —
  // the bulk endpoint rejects them server-side with 423.
  const canApply =
    !!stageResult && committableCount > 0 && !hasConflicts && !isCommitting && !isDemoMode;

  // Why the Apply button is unavailable, shown as a tooltip on its wrapper span.
  const applyHint = canApply
    ? undefined
    : isDemoMode
    ? 'Read-only demo account'
    : hasConflicts
    ? 'Fix the conflicting rates in your file and re-upload before applying'
    : committableCount === 0
    ? 'No product families have rates to apply'
    : undefined;

  // Resolve the effective rates a row will persist. Layered so a partial upload never
  // wipes payers it didn't mention: existing saved rates form the base, the file's rates
  // override the payers it carried, and manual edits win last. Because we commit the full
  // merged set, the backend's whole-row replace preserves untouched payers.
  const effectiveRates = useCallback(
    (r: FeeScheduleStagedFamily): Record<string, number> => {
      const base = existingRatesById[r.product_family_id] ?? {};
      const fileRates = r.status === 'matched' ? r.rates : {};
      const override = editOverrides[r.product_family_id] ?? {};
      return { ...base, ...fileRates, ...override };
    },
    [editOverrides, existingRatesById],
  );

  // How many payers a matched row would actually change vs. what's already saved. A
  // re-upload of identical rates yields 0 — used to label the row "Matches saved rates"
  // instead of the misleading "New rates from file". Compares the incoming payers only
  // (file rates, or a manual override if one exists) against the saved rates.
  const changedPayerCount = useCallback(
    (r: FeeScheduleStagedFamily): number => {
      const existing = existingRatesById[r.product_family_id] ?? {};
      const incoming =
        editOverrides[r.product_family_id] ?? (r.status === 'matched' ? r.rates : {});
      let changed = 0;
      for (const payer of Object.keys(incoming)) {
        const next = incoming[payer] ?? 0;
        const cur = existing[payer];
        if (cur === undefined || round4(cur) !== round4(next)) changed += 1;
      }
      return changed;
    },
    [existingRatesById, editOverrides],
  );

  const handleEditorSave = useCallback((pid: number, rates: Record<string, number>) => {
    setEditOverrides((prev) => ({ ...prev, [pid]: rates }));
    setEditorRow(null);
  }, []);

  const handleApply = useCallback(async () => {
    if (!stageResult || !canApply) return;
    setIsCommitting(true);
    setError(null);
    setSuccess(null);

    const entries: FeeScheduleBulkEntry[] = [];
    for (const r of stageResult.rows) {
      if (r.status === 'conflict') continue; // conflicts are fixed in-file and re-uploaded
      if (r.status === 'no_data' && editOverrides[r.product_family_id] === undefined) continue;
      entries.push({
        product_family_id: r.product_family_id,
        product_family_name: r.product_family_name,
        reimbursements: effectiveRates(r),
      });
    }

    try {
      await ApiService.bulkCommitFeeSchedules(accountNumber, { entries });
      setSuccess(`Saved fee schedule rates for ${entries.length} product famil${entries.length !== 1 ? 'ies' : 'y'}.`);
      setStageResult(null);
      setFileName('');
      setEditOverrides({});
      setParseWarnings(null);
      onCommitted?.();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsCommitting(false);
    }
  }, [stageResult, canApply, editOverrides, effectiveRates, accountNumber, onCommitted]);

  return (
    <div className="payer-upload-tab-body">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        aria-label="Upload fee schedule file"
      />

      {/* Rate editor dialog — rendered outside the result section so z-index stacks correctly */}
      {/* allGroups is always master group names because staged rates are keyed by master group. */}
      {editorRow && stageResult && (
        <RateEditorDialog
          row={editorRow}
          allGroups={resolvedGroups.length > 0
            ? resolvedGroups
            : [
                ...stageResult.columns.matched,
                ...stageResult.columns.missing_groups,
              ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
          }
          initialRates={effectiveRates(editorRow)}
          missingGroups={stageResult.columns.missing_groups}
          isConflict={editorRow.status === 'conflict'}
          mode={stagedMode ?? undefined}
          planRates={editorRow.plan_rates}
          planToGroup={stageResult.columns.plan_to_group}
          hasExistingRates={
            editorRow.status === 'no_data' &&
            editOverrides[editorRow.product_family_id] === undefined &&
            !!existingRatesById[editorRow.product_family_id]
          }
          existingRates={existingRatesById[editorRow.product_family_id]}
          onSave={handleEditorSave}
          onClose={() => setEditorRow(null)}
        />
      )}

      {!stageResult && !isStaging && (
        <>
          {/* ── Upload mode toggle ──────────────────────────────────────────── */}
          <div className="payer-upload-mode-row">
            <div className="payer-upload-mode-toggle" role="group" aria-label="Column grouping">
              {MODE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`payer-upload-mode-opt${uploadMode === opt.value ? ' payer-upload-mode-opt--active' : ''}`}
                  onClick={() => setUploadMode(opt.value)}
                  aria-pressed={uploadMode === opt.value}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <details className="payer-upload-instructions">
            <summary className="payer-upload-instructions-summary">
              <i className="pi pi-info-circle" />
              <span>How to bulk-upload a fee schedule</span>
              <i className="pi pi-chevron-down payer-upload-chevron" />
            </summary>
            <div className="payer-upload-instructions-body">
              <div className="payer-upload-steps">
                <div className="payer-upload-step">
                  <span className="payer-upload-step-num">1</span>
                  <div className="payer-upload-step-body">
                    <span className="payer-upload-step-title">Download the template</span>
                    {uploadMode === 'payer_primary' ? (
                      <span className="payer-upload-step-desc">
                        Get an Excel file with a <strong>J-Code (HCPCS)</strong> column plus one column for every
                        insurance payer primary across all categories. The <strong>J&#8203;0000-SAMPLE</strong> rows are
                        just examples of the format &mdash; replace them with your own J-Codes and rates (column order doesn&rsquo;t matter, only the header names do).
                      </span>
                    ) : (
                      <span className="payer-upload-step-desc">Get an Excel file with a <strong>J-Code (HCPCS)</strong> column plus one column for every payer group, titled to match your payer mix. The <strong>J&#8203;0000-SAMPLE</strong> rows are just examples of the format &mdash; replace them with your own J-Codes and rates (column order doesn&rsquo;t matter, only the header names do).</span>
                    )}
                  </div>
                </div>
                <div className="payer-upload-step">
                  <span className="payer-upload-step-num">2</span>
                  <div className="payer-upload-step-body">
                    <span className="payer-upload-step-title">Fill in the rates</span>
                    {uploadMode === 'payer_primary' ? (
                      <span className="payer-upload-step-desc">
                        Enter the per-Billing Unit reimbursement for each J-Code and insurance payer primary.
                        When multiple primaries belong to the same master payer group, their rates are <strong>averaged</strong> automatically.
                      </span>
                    ) : (
                      <span className="payer-upload-step-desc">Enter the per-Billing Unit reimbursement for each J-Code and payer group.</span>
                    )}
                  </div>
                </div>
                <div className="payer-upload-step">
                  <span className="payer-upload-step-num">3</span>
                  <div className="payer-upload-step-body">
                    <span className="payer-upload-step-title">Upload &amp; review</span>
                    <span className="payer-upload-step-desc">We map J-Codes to your carried product families and show a preview before anything is saved.</span>
                  </div>
                </div>
              </div>
            </div>
          </details>

          <div className="payer-upload-actions">
            <Button
              label="Download Template"
              icon="pi pi-download"
              severity="secondary"
              outlined
              onClick={handleDownloadTemplate}
            />
            <Button
              label="Upload Fee Schedule"
              icon="pi pi-upload"
              onClick={() => fileInputRef.current?.click()}
            />
          </div>

          {error && <Message severity="error" text={error} className="w-full mt-3" />}
          {success && <Message severity="success" text={success} className="w-full mt-3" />}
        </>
      )}

      {isStaging && (
        <div className="fee-schedule-status">
          <ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="6" />
        </div>
      )}

      {stageResult && !isStaging && (
        <div className="payer-upload-result">
          <details className="payer-upload-summary" open>
            <summary className="payer-upload-summary-toggle">
              <i className="pi pi-file" />
              <span className="payer-upload-summary-file">{fileName}</span>
              {stagedMode && (
                <span
                  className="payer-upload-mode-badge"
                  style={{ fontSize: '0.75rem', color: 'var(--text-color-secondary)', marginLeft: '0.4rem', fontWeight: 400 }}
                >
                  {stagedMode === 'payer_primary' ? '(Payer Primaries)' : '(Master Groups)'}
                </span>
              )}
              <i className="pi pi-chevron-down payer-upload-summary-chevron" />
            </summary>
            <div className="payer-upload-summary-body">
              {(() => {
                const changedRows = stageResult.rows.filter(
                  (r) => r.status === 'matched' && changedPayerCount(r) > 0,
                );
                const unchangedRows = stageResult.rows.filter(
                  (r) => r.status === 'matched' && changedPayerCount(r) === 0,
                );
                return (
                  <>
                    <div className="payer-upload-summary-row">
                      <i className="pi pi-check-circle payer-upload-icon-success" />
                      <span>
                        <strong>{changedRows.length}</strong> product famil
                        {changedRows.length !== 1 ? 'ies' : 'y'} with new or updated rates from the file
                      </span>
                    </div>
                    {unchangedRows.length > 0 && (
                      <div className="payer-upload-summary-row">
                        <i className="pi pi-check-circle" style={{ color: 'var(--text-color-secondary)' }} />
                        <span>
                          <strong>{unchangedRows.length}</strong> product famil
                          {unchangedRows.length !== 1 ? 'ies' : 'y'} already match the file — no changes
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
              {(() => {
                const keptRows = stageResult.rows.filter(
                  (r) => r.status === 'no_data' && !!existingRatesById[r.product_family_id],
                );
                const noRateRows = stageResult.rows.filter(
                  (r) => r.status === 'no_data' && !existingRatesById[r.product_family_id],
                );
                return (
                  <>
                    {keptRows.length > 0 && (
                      <div className="payer-upload-summary-row">
                        <i className="pi pi-info-circle" style={{ color: 'var(--blue-500)' }} />
                        <span>
                          <strong>{keptRows.length}</strong> product famil
                          {keptRows.length !== 1 ? 'ies' : 'y'} not in file — existing saved rates kept
                        </span>
                      </div>
                    )}
                    {noRateRows.length > 0 && (
                      <div className="payer-upload-summary-row">
                        <i className="pi pi-exclamation-circle" style={{ color: 'var(--orange-400)' }} />
                        <span>
                          <strong>{noRateRows.length}</strong> product famil
                          {noRateRows.length !== 1 ? 'ies' : 'y'} not in file — no rates found (Medicare fallback)
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
              {stageResult.conflicts.length > 0 && (
                <div className="payer-upload-summary-row">
                  <i className="pi pi-exclamation-triangle payer-upload-icon-changed" />
                  <span>
                    <strong>{stageResult.conflicts.length}</strong> conflict
                    {stageResult.conflicts.length !== 1 ? 's' : ''} — fix in your file and re-upload
                  </span>
                </div>
              )}
            </div>
          </details>

          {error && <Message severity="error" text={error} className="w-full" />}

          {stageResult.rows.length === 0 && (
            <Message
              severity="warn"
              text="No active product families were found for this account. Check that the account has current distribution data set up before uploading a fee schedule."
              className="w-full"
            />
          )}

          {/* Prominent banner when the whole file matched nothing (likely wrong format/account). */}
          {parseWarnings?.allUnmatched && (
            <Message
              severity="warn"
              text="None of the J-Codes in your file matched your product families, so no rates were imported. Check that the first column contains J-Codes (HCPCS), the first row is the column header, and that this fee schedule is for the correct account. You can still enter rates manually below."
              className="w-full"
            />
          )}

          {/* Data warnings — surface silent rewrites / dropped data from parsing & matching. */}
          {parseWarnings &&
            (parseWarnings.duplicateHeaders.length > 0 ||
              parseWarnings.duplicateJcodes.length > 0 ||
              parseWarnings.unmatchedJcodes.length > 0 ||
              parseWarnings.negativeClamped > 0 ||
              parseWarnings.maxClamped > 0) && (
              <details className="payer-upload-warnings" open>
                <summary className="payer-upload-warnings-summary">
                  <i className="pi pi-exclamation-triangle" style={{ color: 'var(--orange-400)' }} />
                  Data warnings — review before applying
                  <i className="pi pi-chevron-down payer-upload-warnings-chevron" />
                </summary>
                <ul className="payer-upload-warnings-body">
                  {parseWarnings.duplicateHeaders.length > 0 && (
                    <li className="payer-upload-warning-item">
                      Duplicate column{parseWarnings.duplicateHeaders.length !== 1 ? 's' : ''}{' '}
                      <strong>{parseWarnings.duplicateHeaders.join(', ')}</strong> appeared more than
                      once — their values were <strong>averaged</strong> per J-Code. If these were meant
                      to be different payers, rename the columns and re-upload.
                    </li>
                  )}
                  {parseWarnings.duplicateJcodes.length > 0 && (
                    <li className="payer-upload-warning-item">
                      J-Code{parseWarnings.duplicateJcodes.length !== 1 ? 's' : ''}{' '}
                      <strong>{parseWarnings.duplicateJcodes.join(', ')}</strong> appeared in more than
                      one row. Rows with the same rate were merged automatically. Any rows with{' '}
                      <em>different</em> rates for a payer are flagged as{' '}
                      <strong>conflicts</strong> below — fix them in your file and re-upload.
                    </li>
                  )}
                  {parseWarnings.unmatchedJcodes.length > 0 && (
                    <li className="payer-upload-warning-item">
                      <strong>{parseWarnings.unmatchedJcodes.length}</strong> J-Code
                      {parseWarnings.unmatchedJcodes.length !== 1 ? 's' : ''} in the file matched none of
                      your product families and{' '}
                      {parseWarnings.unmatchedJcodes.length !== 1 ? 'were' : 'was'} ignored:{' '}
                      <strong>
                        {parseWarnings.unmatchedJcodes.slice(0, 20).join(', ')}
                        {parseWarnings.unmatchedJcodes.length > 20
                          ? `, +${parseWarnings.unmatchedJcodes.length - 20} more`
                          : ''}
                      </strong>
                      .
                    </li>
                  )}
                  {parseWarnings.negativeClamped > 0 && (
                    <li className="payer-upload-warning-item">
                      <strong>{parseWarnings.negativeClamped}</strong> negative rate
                      {parseWarnings.negativeClamped !== 1 ? 's were' : ' was'} found and set to{' '}
                      <strong>$0.00</strong> (which uses the Medicare fallback). Fix the source file if
                      that wasn&apos;t intended.
                    </li>
                  )}
                  {parseWarnings.maxClamped > 0 && (
                    <li className="payer-upload-warning-item">
                      <strong>{parseWarnings.maxClamped}</strong> rate
                      {parseWarnings.maxClamped !== 1 ? 's exceeded' : ' exceeded'} the maximum and{' '}
                      {parseWarnings.maxClamped !== 1 ? 'were' : 'was'} capped at $999,999.99.
                    </li>
                  )}
                </ul>
              </details>
            )}

          {/* Column report */}
          {(stageResult.columns.unrecognized.length > 0 || stageResult.columns.missing_groups.length > 0) && (
            <details className="payer-upload-warnings">
              <summary className="payer-upload-warnings-summary">
                <i className="pi pi-info-circle" />
                Column details
                <i className="pi pi-chevron-down payer-upload-warnings-chevron" />
              </summary>
              <ul className="payer-upload-warnings-body">
                {stageResult.columns.unrecognized.map((c) => (
                  <li key={`unrec-${c}`} className="payer-upload-warning-item">
                    Column <strong>{c}</strong> isn&apos;t a known
                    {uploadMode === 'payer_primary' ? ' insurance payer primary' : ' payer group'} — its rates were ignored.
                  </li>
                ))}
                {stageResult.columns.missing_groups.map((g) => (
                  <li key={`miss-${g}`} className="payer-upload-warning-item">
                    Payer group <strong>{g}</strong> has no
                    {uploadMode === 'payer_primary' ? ' payer primary in the file' : ' column'} — existing saved rates for this group will be preserved.
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* Conflicting rates — read-only. The user fixes the file and re-uploads. */}
          {stageResult.conflicts.length > 0 && (
            <div className="payer-upload-table-wrap">
              <Message
                severity="error"
                className="w-full mb-2"
                content={
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                      Conflicting rates found — nothing can be saved until these are fixed.
                    </div>
                    <div style={{ fontSize: '0.8125rem' }}>
                      A payer has more than one rate for the same product family (from duplicate
                      rows or different J-Codes). Fix the rows below in your file so each payer has
                      one rate, then upload again.
                    </div>
                  </div>
                }
              />
              <table className="payer-upload-table">
                <thead>
                  <tr>
                    <th>Product Family</th>
                    <th>Payer</th>
                    <th>J-Codes</th>
                    <th>Conflicting rates</th>
                  </tr>
                </thead>
                <tbody>
                  {stageResult.conflicts.map((c) => (
                    <tr key={`${c.product_family_id}::${c.payer}`} className="payer-upload-row-changed">
                      <td>{c.product_family_name}</td>
                      <td>{c.payer}</td>
                      <td>{c.jcodes.join(', ')}</td>
                      <td>
                        <span className="fs-conflict-values">
                          {c.values.map((v) => `$${v.toFixed(2)}`).join('  vs  ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Staged families preview */}
          <div className="payer-upload-table-wrap">
            <table className="payer-upload-table">
              <thead>
                <tr>
                  <th>Product Family</th>
                  <th>Category</th>
                  <th>J-Code</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {stageResult.rows.slice(0, 200).map((r) => {
                  const isEdited = editOverrides[r.product_family_id] !== undefined;
                  const hasExisting =
                    r.status === 'no_data' && !!existingRatesById[r.product_family_id];
                  const allZeroRates =
                    r.status === 'matched' &&
                    Object.keys(r.rates).length > 0 &&
                    Object.keys(r.rates).every((k) => (r.rates[k] ?? 0) === 0);
                  const matchedUnchanged =
                    r.status === 'matched' && changedPayerCount(r) === 0;
                  return (
                    <tr key={r.product_family_id}>
                      <td>
                        <button
                          type="button"
                          className="fee-schedule-item fs-upload-pf-btn"
                          style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', color: 'var(--primary-color)', fontWeight: isEdited ? 600 : 400 }}
                          onClick={() => setEditorRow(r)}
                          data-pr-tooltip="Click to view or edit rates"
                          data-pr-position="top"
                        >
                          {isEdited && <i className="pi pi-pencil" style={{ fontSize: '0.7rem', marginRight: '0.35rem', verticalAlign: 'middle' }} />}
                          {r.product_family_name}
                        </button>
                      </td>
                      <td>{r.category ?? '—'}</td>
                      <td>{r.jcodes.length > 0 ? r.jcodes.join(', ') : '—'}</td>
                      <td>{statusTag(r.status, isEdited, hasExisting, allZeroRates, matchedUnchanged)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {stageResult.rows.length > 200 && (
              <p className="text-500 text-center mt-2" style={{ fontSize: '0.8125rem' }}>
                Showing first 200 of {stageResult.rows.length} product families
              </p>
            )}
            <Tooltip target=".fs-upload-pf-btn,.fs-upload-zero-rate" />
          </div>

          {/* Footer actions */}
          <div className="payer-upload-footer">
            <Button
              label="Upload Different File"
              icon="pi pi-upload"
              severity="secondary"
              text
              onClick={() => {
                resetState();
                setTimeout(() => fileInputRef.current?.click(), 0);
              }}
              disabled={isCommitting}
            />
            <div className="payer-upload-footer-right">
              <Button
                label="Discard"
                icon="pi pi-times"
                severity="secondary"
                outlined
                onClick={handleDiscard}
                disabled={isCommitting}
              />
              {/* The hint sits on this span rather than on the Button. A native `title`
                  is not rendered on a disabled <button>, and PrimeReact's showOnDisabled
                  fixes that by re-parenting the button into a wrapper div outside React,
                  which then crashes on unmount. A plain span does neither. */}
              <Tooltip target=".fs-upload-apply-tip" position="top" />
              <span
                className={`inline-flex${applyHint ? ' fs-upload-apply-tip' : ''}`}
                data-pr-tooltip={applyHint}
              >
                <Button
                  label={isCommitting ? 'Applying...' : `Apply${committableCount > 0 ? ` (${committableCount})` : ''}`}
                  icon={isCommitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                  onClick={handleApply}
                  disabled={!canApply}
                />
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeScheduleUpload;
